import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillCheckSquare } from 'react-icons/ai';
import { BiCommentError, BiRefresh } from 'react-icons/bi';
import {
  BsCardChecklist,
  BsCardList,
  BsFileEarmarkCheck,
  BsFileEarmarkSpreadsheet,
  BsFileRichtext,
  BsUnlock,
} from 'react-icons/bs';
import {
  FaCheck,
  FaDownload,
  FaEye,
  FaFileCsv,
  FaMapMarkerAlt,
  FaPlus,
  FaRegClone,
  FaRegFolderOpen,
  FaRegTrashAlt,
  FaSearch,
  FaSeedling,
  FaUserTie,
} from 'react-icons/fa';
import { FcHighPriority, FcMediumPriority, FcPlus, FcSearch, FcSurvey } from 'react-icons/fc';
import { FiEdit } from 'react-icons/fi';
import { GoCloudUpload } from 'react-icons/go';

import { useAbility } from '@/hooks/ability';
import { cn } from '@/lib/utils';
import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ScanEye } from 'lucide-react';

import { FakeIcon, SIcon, SIconButton, SIconContainer } from './styles';
import { IconWithTooltipProps } from './types';

export const IconWithTooltip: React.FC<
  IconWithTooltipProps & { tabIndex?: number; button?: boolean; permission?: string }
> = ({
  action,
  className,
  title,
  loading,
  placement = 'top',
  visibility,
  color,
  tabIndex,
  button = true,
  permission,
  ...rest
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const hasPermission = !permission || ability.can(permission, '');

  const renderIconLabel = (action: string) => {
    switch (action) {
      case 'edit':
        return t('edit');
      case 'destroy':
        return t('remove');
      case 'view':
        return t('view');
      case 'download':
        return t('download.title');
      default:
        return '';
    }
  };

  const renderIconAction = (type: string) => {
    switch (type) {
      case 'beneficiary':
        return <FaUserTie />;
      case 'csv':
        return <FaFileCsv />;
      case 'create':
        return <FaPlus />;
      case 'edit':
        return <FiEdit />;
      case 'preview-watermark':
        return <ScanEye />;
      case 'view':
        return <FaEye />;
      case 'search':
        return <FaSearch />;
      case 'detail':
        return <FaEye />;
      case 'destroy':
        return <FaRegTrashAlt />;
      case 'permission':
        return <BsUnlock />;
      case 'download':
        return <FaDownload />;
      case 'upload':
        return <GoCloudUpload />;
      case 'farm':
        return <FaSeedling />;
      case 'attachment':
        return <FaRegFolderOpen />;
      case 'registration':
        return <BsCardList />;
      case 'averbation':
        return <BsFileEarmarkCheck />;
      case 'license':
        return <BsCardChecklist />;
      case 'registrationArea':
        return <BsFileEarmarkSpreadsheet />;
      case 'registrationField':
        return <BsFileRichtext />;
      case 'approval':
        return <AiFillCheckSquare />;
      case 'check':
        return <FaCheck />;
      case 'medium-priority':
        return <FcMediumPriority />;
      case 'high-priority':
        return <FcHighPriority />;
      case 'survey':
        return <FcSurvey />;
      case 'plus':
        return <FcPlus />;
      case 'fc-search':
        return <FcSearch />;
      case 'clone':
        return <FaRegClone />;
      case 'refresh':
        return <BiRefresh />;
      case 'error':
        return <BiCommentError />;
      case 'fake':
        return <FakeIcon />;
      case 'location':
        return <FaMapMarkerAlt />;
      default:
        return null;
    }
  };

  if (!hasPermission) return null;

  return (
    <SIconContainer className={className}>
      <Tooltip>
        <TooltipTrigger asChild>
          {button ? (
            <SIconButton
              {...rest}
              type="button"
              tabIndex={tabIndex}
              className={cn(visibility, 'hover:opacity-50 focus:opacity-50')}
              style={{ color }}
              title={renderIconLabel(action)}
              onClick={loading ? undefined : (rest.onClick ?? undefined)}
            >
              {loading ? <LoadingOutlined style={{ fontSize: 20, color: '#FFA23C' }} spin /> : renderIconAction(action)}
            </SIconButton>
          ) : (
            <SIcon
              {...rest}
              tabIndex={tabIndex}
              className={cn(visibility, 'hover:opacity-50 focus:opacity-50')}
              style={{ color }}
              title={renderIconLabel(action)}
              onClick={loading ? undefined : (rest.onClick ?? undefined)}
            >
              {loading ? <LoadingOutlined style={{ fontSize: 20, color: '#FFA23C' }} spin /> : renderIconAction(action)}
            </SIcon>
          )}
        </TooltipTrigger>
        <TooltipContent side={placement as any}>
          {title}
        </TooltipContent>
      </Tooltip>
    </SIconContainer>
  );
};

export default IconWithTooltip;
